import { ActionContext } from 'vuex';
import axiosAuth from '@/config/axios';
import { API_URL } from '@/config/api';
import { Client, IFilterClient } from '../types/client';
import { useNotification } from '@/composables/useNotification';
import { FormMode, defaultClient, defaultFilterClient } from '../consts';
import { ClientState, ImportBonificadosSummaryItem, ImportBonificadosExecuteResult, ImportBonificadosRow } from '../types/state';
import { ClientServices } from '@/modules/client_services/types/client_services';
import { ClientAddresses } from '@/modules/client_addresses/types/client_addresses';
import { DefaultResponse, DefaultResponseItem } from '@/types/response';
import { IRootState } from '@/types/state';
import { ClientResponsible } from '@/modules/responsibles/types/responsibles';
import { ClientComplaints } from '../../client_complaints/types/complaints';
import { createClient } from '@supabase/supabase-js';
import { ScreensClientEmployeeUse } from '@/modules/client_employee/consts';


const { notifySuccess, notifyError } = useNotification();

/** Garante camelCase no summary (ex.: clean_kit_size → cleanKitSize) para exibição no preview. */
function normalizePreviewSummary(summary: ImportBonificadosSummaryItem[]): ImportBonificadosSummaryItem[] {
  return summary.map((item) => {
    const raw = item as ImportBonificadosSummaryItem & {
      clean_kit_size?: string;
      client_status_in_system?: number | null;
      import_will_apply_client_status?: boolean;
      import_will_change_client_status?: boolean;
      inactivate_only?: boolean;
      preview_note?: string;
      address_street_in_system?: string | null;
      address_number_in_system?: string | null;
      address_city_in_system?: string | null;
      address_state_in_system?: string | null;
      address_neighborhood_in_system?: string | null;
      responsible_name_in_system?: string | null;
      responsible_apartment_in_system?: string | null;
      responsible_phone?: string;
      responsible_phone_in_system?: string | null;
      import_will_update_address?: boolean;
      replace_responsible?: boolean;
      address_cep_in_system?: string | null;
      responsible_complement_is_professional?: boolean;
      address_cep?: string;
    };
    const cleanKitSize = raw.cleanKitSize ?? raw.clean_kit_size ?? undefined;
    const clientStatusInSystem =
      raw.clientStatusInSystem !== undefined ? raw.clientStatusInSystem : raw.client_status_in_system;
    const importWillApplyClientStatus =
      raw.importWillApplyClientStatus ?? raw.import_will_apply_client_status;
    const importWillChangeClientStatus =
      raw.importWillChangeClientStatus ?? raw.import_will_change_client_status;
    const inactivateOnly = raw.inactivateOnly ?? raw.inactivate_only;
    const previewNote = raw.previewNote ?? raw.preview_note;
    const addressStreetInSystem = raw.addressStreetInSystem ?? raw.address_street_in_system;
    const addressNumberInSystem = raw.addressNumberInSystem ?? raw.address_number_in_system;
    const addressCityInSystem = raw.addressCityInSystem ?? raw.address_city_in_system;
    const addressStateInSystem = raw.addressStateInSystem ?? raw.address_state_in_system;
    const addressNeighborhoodInSystem =
      raw.addressNeighborhoodInSystem ?? raw.address_neighborhood_in_system;
    const responsibleNameInSystem = raw.responsibleNameInSystem ?? raw.responsible_name_in_system;
    const responsibleApartmentInSystem =
      raw.responsibleApartmentInSystem ?? raw.responsible_apartment_in_system;
    const responsiblePhone = raw.responsiblePhone ?? raw.responsible_phone;
    const responsiblePhoneInSystem =
      raw.responsiblePhoneInSystem ?? raw.responsible_phone_in_system;
    const importWillUpdateAddress =
      raw.importWillUpdateAddress ?? raw.import_will_update_address;
    const replaceResponsible = raw.replaceResponsible ?? raw.replace_responsible;
    const addressCepInSystem = raw.addressCepInSystem ?? raw.address_cep_in_system;
    const responsibleComplementIsProfessional =
      raw.responsibleComplementIsProfessional ?? raw.responsible_complement_is_professional;
    const addressCep = raw.addressCep ?? raw.address_cep;
    return {
      ...item,
      cleanKitSize,
      ...(clientStatusInSystem !== undefined ? { clientStatusInSystem } : {}),
      ...(importWillApplyClientStatus !== undefined ? { importWillApplyClientStatus } : {}),
      ...(importWillChangeClientStatus !== undefined ? { importWillChangeClientStatus } : {}),
      ...(inactivateOnly !== undefined ? { inactivateOnly } : {}),
      ...(previewNote !== undefined ? { previewNote } : {}),
      ...(addressStreetInSystem !== undefined ? { addressStreetInSystem } : {}),
      ...(addressNumberInSystem !== undefined ? { addressNumberInSystem } : {}),
      ...(addressCityInSystem !== undefined ? { addressCityInSystem } : {}),
      ...(addressStateInSystem !== undefined ? { addressStateInSystem } : {}),
      ...(addressNeighborhoodInSystem !== undefined ? { addressNeighborhoodInSystem } : {}),
      ...(responsibleNameInSystem !== undefined ? { responsibleNameInSystem } : {}),
      ...(responsibleApartmentInSystem !== undefined ? { responsibleApartmentInSystem } : {}),
      ...(responsiblePhone !== undefined ? { responsiblePhone } : {}),
      ...(responsiblePhoneInSystem !== undefined ? { responsiblePhoneInSystem } : {}),
      ...(importWillUpdateAddress !== undefined ? { importWillUpdateAddress } : {}),
      ...(replaceResponsible !== undefined ? { replaceResponsible } : {}),
      ...(addressCepInSystem !== undefined ? { addressCepInSystem } : {}),
      ...(responsibleComplementIsProfessional !== undefined
        ? { responsibleComplementIsProfessional }
        : {}),
      ...(addressCep !== undefined ? { addressCep } : {}),
    };
  });
}

const state: () => ClientState = () => ({
  addresses: [],
  clients: [],
  clientsByEmployee: [],
  clientResponsible: null,
  clientResponsiblesHistory: [],
  services: [],
  currentClientId: '',
  filteredClients: [],
  filterClient: Object.assign({}, defaultFilterClient),
  formMode: FormMode.Add,
  isCreateDialogOpen: false,
  isFilterDialogOpen: false,
  isImportBonificadosDialogOpen: false,
  importBonificadosRows: null,
  importBonificadosPreviewSummary: null,
  importBonificadosExecuteResult: null,
  isCompareDaysDialogOpen: false,
  compareDaysPreviewSummary: null,
  compareDaysRows: null,
  pagination: {
    page: 1,
    itemsPerPage: 10,
    total: 0,
  },
  paginationServices: {
    page: 1,
    itemsPerPage: 10,
    total: 0,
  },
  showDeleteDialog: false,
  selectedClient: defaultClient,
  selectedClientEmployees: [],
});

const getters = {
  getCurrentClientId: (state: ClientState) => {
    return state.currentClientId;
  },
}

const mutations = {
  SET_CURRENT_CLIENT_ID(state: ClientState, payload: { clientId: string }) {
    state.currentClientId = payload.clientId;
  },
  SET_CLIENTS(state: ClientState, payload: { clients: Client[] }) {
    state.clients = payload.clients;
    state.filteredClients = payload.clients;
  },
  SET_FILTER_CLIENT(state: ClientState, filterClient: IFilterClient) {
    state.filterClient = filterClient;
  },
  SET_FILTERED_CLIENTS(state: ClientState, filteredClients: Client[]) {
    state.filteredClients = filteredClients;
  },
  SET_CREATE_DIALOG_OPEN(state: ClientState, isOpen: boolean) {
    state.isCreateDialogOpen = isOpen;
  },
  SET_FILTER_DIALOG_OPEN(state: ClientState, isOpen: boolean) {
    state.isFilterDialogOpen = isOpen;
  },
  SET_SELECTED_CLIENT(state: ClientState, client: Client) {
    state.selectedClient = Object.assign({}, client);
  },
  SET_FORM_MODE(state: ClientState, mode: FormMode) {
    state.formMode = mode;
  },
  SET_ADDRESSES(state: ClientState, addresses: ClientAddresses[]) {
    state.addresses = addresses;
  },
  SET_SERVICES(state: ClientState, addresses: ClientServices[]) {
    state.services = addresses;
  },
  SET_TOTAL_ITEMS(state: ClientState, payload: { total: number }) {
    state.pagination.total = payload.total;
  },
  SET_CLIENT_RESPONSIBLE(state: ClientState, clientResponsible: ClientResponsible) {
    state.clientResponsible = clientResponsible;
  },
  SET_CLIENT_RESPONSIBLES_HISTORY(state: ClientState, payload: { history: ClientResponsible[] }) {
    state.clientResponsiblesHistory = payload.history ?? [];
  },
  SET_CLIENTS_BY_EMPLOYEE(state: ClientState, payload: { clientsByEmployee: Client[] }) {
    state.clientsByEmployee = payload.clientsByEmployee;
  },  
  SHOW_DELETE_DIALOG(state: ClientState, payload: { show: boolean }) {
    state.showDeleteDialog = payload.show;
  },
  RESET_CLIENT_FILTER(state: ClientState) {
    state.filterClient = Object.assign({}, defaultFilterClient);
  },
  SET_IMPORT_BONIFICADOS_DIALOG_OPEN(state: ClientState, payload: { open: boolean }) {
    state.isImportBonificadosDialogOpen = payload.open;
    if (!payload.open) {
      state.importBonificadosRows = null;
      state.importBonificadosPreviewSummary = null;
      state.importBonificadosExecuteResult = null;
    }
  },
  SET_IMPORT_BONIFICADOS_PREVIEW(state: ClientState, payload: { rows: ImportBonificadosRow[]; summary: ImportBonificadosSummaryItem[] }) {
    state.importBonificadosRows = payload.rows;
    state.importBonificadosPreviewSummary = normalizePreviewSummary(payload.summary);
    state.importBonificadosExecuteResult = null;
  },
  SET_IMPORT_BONIFICADOS_RESULT(state: ClientState, payload: { result: ImportBonificadosExecuteResult }) {
    state.importBonificadosExecuteResult = payload.result;
  },
  SET_COMPARE_DAYS_DIALOG_OPEN(state: ClientState, payload: { open: boolean }) {
    state.isCompareDaysDialogOpen = payload.open;
    if (!payload.open) {
      state.compareDaysPreviewSummary = null;
      state.compareDaysRows = null;
    }
  },
  SET_COMPARE_DAYS_PREVIEW(state: ClientState, payload: { rows: ImportBonificadosRow[]; summary: ImportBonificadosSummaryItem[] }) {
    state.compareDaysRows = payload.rows;
    state.compareDaysPreviewSummary = normalizePreviewSummary(payload.summary);
  },
};

const actions = {
  clearFilter({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_FILTER_CLIENT', Object.assign({}, defaultFilterClient));
  },
  async fetchClientsById({ commit }: ActionContext<ClientState, IRootState>, payload: { clientId: string }) {
    try {
      const response = await axiosAuth.get<Client>(`${API_URL}/clients/${payload.clientId}`);
      commit('SET_SELECTED_CLIENT', response.data);
    } catch (error) {
      notifyError('Erro ao buscar cliente');
      console.error('Erro ao buscar cliente:', error);
    }
  },
  async fetchClients({ commit, state }: ActionContext<ClientState, IRootState>) {
    try {
      const params: any = {};

      for (const key in state.filterClient) {
        const value = state.filterClient[key as keyof typeof state.filterClient];
        if (value || value === 0) {
          params[key] = value;
        }
      }
      params.page = state.pagination.page;
      params.itemsPerPage = state.pagination.itemsPerPage;
      const response = await axiosAuth.get<DefaultResponse<Client>>(`${API_URL}/clients`,
        {
          params
        }
      );
      commit('SET_TOTAL_ITEMS', { total: response.data.total });
      commit('SET_CLIENTS', { clients: response.data.data });
    } catch (error) {
      notifyError('Erro ao buscar clientes');
      console.error('Erro ao buscar clientes:', error);
    }
  },
  async createClient({ dispatch, state }: ActionContext<ClientState, IRootState>) {
    const { ...clientToSave } = state.selectedClient;
    delete clientToSave.id;
    delete clientToSave.createdAt;
    try {
      await axiosAuth.post(`${API_URL}/clients`, clientToSave);
      notifySuccess('Cliente criado com sucesso');
      dispatch('fetchClients');
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      notifyError('Erro');
    }
  },
  async updateClient({ dispatch, state }: ActionContext<ClientState, IRootState>) {
    const { id, ...clientToUpdate } = state.selectedClient;
    try {
      await axiosAuth.put(`${API_URL}/clients/${id}`, clientToUpdate);
      notifySuccess('Cliente atualizado com sucesso');
      dispatch('fetchClients');
    } catch (error) {
      notifyError('Erro');
      console.error('Erro ao atualizar cliente:', error);
    }
  },
  async changeStatusClient({ dispatch }: ActionContext<ClientState, IRootState>, id: string) {
    try {
      await axiosAuth.put(`${API_URL}/clients/${id}/status`);
      notifySuccess('Status do cliente alterado com sucesso');
      dispatch('fetchClients');
    } catch (error) {
      notifyError('Erro');
      console.error('Erro ao alterar status do cliente:', error);
    }
  },
  async deleteClient({ commit, dispatch, state }: ActionContext<ClientState, IRootState>) {
    try {
      await axiosAuth.delete(`${API_URL}/clients/${state.selectedClient.id}`);
      notifySuccess('Cliente deletado com sucesso');
      dispatch('fetchClients');
      commit('SHOW_DELETE_DIALOG', { show: false });
    } catch (error) {
      notifyError('Erro');
      console.error('Erro ao deletar cliente:', error);
    }
  },
  openDeleteDialog({ commit }: ActionContext<ClientState, IRootState>, payload: { client: Client }) {
      commit('SHOW_DELETE_DIALOG', { show: true });
      commit('SET_SELECTED_CLIENT', payload.client);
  },
  openFilterDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_FILTER_DIALOG_OPEN', true);

  },
  closeFilterDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_FILTER_DIALOG_OPEN', false);
  },
  async fetchClientResponsible({ commit }: ActionContext<ClientState, IRootState>, payload: { clientId: string }) {
    try { 
      const result = await axiosAuth.get<DefaultResponseItem<ClientResponsible>>(`${API_URL}/responsibles/client_active/${payload.clientId}`);
      commit('SET_CLIENT_RESPONSIBLE', result.data.data);
    } catch(error) {
      console.error('Erro ao buscar responsável do cliente:', error);
      notifyError('Erro ao buscar responsável do cliente');
    }
  },
  async fetchClientResponsiblesHistory({ commit }: ActionContext<ClientState, IRootState>, payload: { clientId: string }) {
    try {
      const result = await axiosAuth.get<DefaultResponseItem<ClientResponsible[]>>(`${API_URL}/responsibles/client_history/${payload.clientId}`);
      commit('SET_CLIENT_RESPONSIBLES_HISTORY', { history: result.data?.data ?? [] });
    } catch (error) {
      console.error('Erro ao buscar histórico de responsáveis:', error);
      notifyError('Erro ao buscar histórico de responsáveis');
      commit('SET_CLIENT_RESPONSIBLES_HISTORY', { history: [] });
    }
  },
  async fetchClientsByEmployee({ commit }: ActionContext<ClientState, IRootState>, payload: { employeeId: string }) {
    try {
      const url = `${API_URL}/client_employee/clients/by-employee/${payload.employeeId}`;
      const response = await axiosAuth.get<DefaultResponse<Client>>(`${url}`);
      
      commit('SET_CLIENTS_BY_EMPLOYEE', { clientsByEmployee: response.data.data });
    } catch (error) {
      notifyError('Erro ao buscar os dados');
      console.error('Erro ao buscar os dados:', error);
    }
  },
  openCreateDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_FORM_MODE', FormMode.Add);
    commit('SET_SELECTED_CLIENT', defaultClient);
    commit('SET_CREATE_DIALOG_OPEN', true);
  },
  openEditDialog({ commit }: ActionContext<ClientState, IRootState>, client: Client) {
    if(typeof client.startedAsClient == 'string') {
      client.startedAsClient = client.startedAsClient ? new Date(client.startedAsClient) : '';
    }
    commit('SET_FORM_MODE', FormMode.Edit);
    commit('SET_SELECTED_CLIENT', client);
    commit('SET_CREATE_DIALOG_OPEN', true);
  },
  async openScreenDetails({ dispatch, commit }: ActionContext<ClientState, IRootState>, payload: { clientId: string }) {
    try {
      commit('SET_CURRENT_CLIENT_ID', { clientId: payload.clientId })

      dispatch('fetchClientsById', { clientId: payload.clientId });
      dispatch('client_addresses/getClientAddresses', { clientId: payload.clientId }, { root: true });
      dispatch('client_services/getClientServices', { clientId: payload.clientId }, { root: true });
      dispatch('services/fetchServices', {}, { root: true });
      dispatch('client_employee/fetchClientsEmployees', { clientId: payload.clientId, screen: ScreensClientEmployeeUse.CLIENT}, { root: true });
    } catch (error) {
      notifyError('Erro ao buscar informações do cliente');
      console.error('Erro ao buscar informações do cliente:', error);
    }
  },




  openViewDialog({ commit }: ActionContext<ClientState, IRootState>, client: Client) {
    commit('SET_FORM_MODE', FormMode.View);
    commit('SET_SELECTED_CLIENT', client);
    commit('SET_CREATE_DIALOG_OPEN', true);
  },
  closeCreateDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_CREATE_DIALOG_OPEN', false);
  },
  openImportBonificadosDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_IMPORT_BONIFICADOS_DIALOG_OPEN', { open: true });
  },
  closeImportBonificadosDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_IMPORT_BONIFICADOS_DIALOG_OPEN', { open: false });
  },
  openCompareDaysDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_COMPARE_DAYS_DIALOG_OPEN', { open: true });
  },
  closeCompareDaysDialog({ commit }: ActionContext<ClientState, IRootState>) {
    commit('SET_COMPARE_DAYS_DIALOG_OPEN', { open: false });
  },
  async fetchCompareDaysPreview({ commit }: ActionContext<ClientState, IRootState>, payload: { file: File }) {
    try {
      const formData = new FormData();
      formData.append('file', payload.file);
      const response = await axiosAuth.post<{ summary: ImportBonificadosSummaryItem[]; rows: ImportBonificadosRow[] }>(
        `${API_URL}/clients/import-bonificados/preview`,
        formData
      );
      commit('SET_COMPARE_DAYS_PREVIEW', { rows: response.data.rows, summary: response.data.summary });
    } catch (error: any) {
      const message = error?.response?.data?.error ?? error?.response?.data?.message ?? 'Erro ao processar planilha';
      notifyError(typeof message === 'string' ? message : 'Erro ao gerar resumo da comparação');
      console.error('Erro ao gerar resumo da comparação:', error);
      throw error;
    }
  },
  async fetchImportBonificadosPreview({ commit }: ActionContext<ClientState, IRootState>, payload: { file: File }) {
    try {
      const formData = new FormData();
      formData.append('file', payload.file);
      const response = await axiosAuth.post<{ summary: ImportBonificadosSummaryItem[]; rows: ImportBonificadosRow[] }>(
        `${API_URL}/clients/import-bonificados/preview`,
        formData
      );
      commit('SET_IMPORT_BONIFICADOS_PREVIEW', { rows: response.data.rows, summary: response.data.summary });
    } catch (error: any) {
      const message = error?.response?.data?.error ?? error?.response?.data?.message ?? 'Erro ao processar planilha';
      notifyError(typeof message === 'string' ? message : 'Erro ao gerar resumo da importação');
      console.error('Erro ao gerar resumo da importação:', error);
      throw error;
    }
  },
  async executeImportBonificados(
    { commit, state, dispatch }: ActionContext<ClientState, IRootState>,
    payload: { rows?: ImportBonificadosRow[]; skipClientDocs?: string[]; skipRowIndexes?: number[]; skipServiceByRow?: { rowIndex: number; serviceId: string }[] }
  ) {
    const rowsToSend = payload.rows ?? state.importBonificadosRows;
    if (!rowsToSend?.length) {
      notifyError('Nenhum dado para importar');
      return;
    }
    try {
      const batchSize = 25;
      const aggregatedResult: ImportBonificadosExecuteResult = {
        clientsCreated: 0,
        servicesCreated: 0,
        servicesUpdated: 0,
        servicesDeleted: 0,
        errors: [],
      };

      for (let start = 0; start < rowsToSend.length; start += batchSize) {
        const rowsBatch = rowsToSend.slice(start, start + batchSize);
        const rowIndexes = new Set(rowsBatch.map((row) => row.rowIndex));
        const docs = new Set(rowsBatch.map((row) => row.doc));
        const response = await axiosAuth.post<ImportBonificadosExecuteResult>(
          `${API_URL}/clients/import-bonificados/execute`,
          {
            rows: rowsBatch,
            skipClientDocs: payload.skipClientDocs?.filter((doc) => docs.has(doc)),
            skipRowIndexes: payload.skipRowIndexes?.filter((rowIndex) => rowIndexes.has(rowIndex)),
            skipServiceByRow: payload.skipServiceByRow?.filter(({ rowIndex }) => rowIndexes.has(rowIndex)),
          }
        );

        aggregatedResult.clientsCreated += response.data.clientsCreated;
        aggregatedResult.servicesCreated += response.data.servicesCreated;
        aggregatedResult.servicesUpdated =
          (aggregatedResult.servicesUpdated ?? 0) + (response.data.servicesUpdated ?? 0);
        aggregatedResult.servicesDeleted =
          (aggregatedResult.servicesDeleted ?? 0) + (response.data.servicesDeleted ?? 0);
        if (response.data.errors?.length) {
          aggregatedResult.errors?.push(...response.data.errors);
        }
      }

      commit('SET_IMPORT_BONIFICADOS_RESULT', { result: aggregatedResult });
      const parts = [
        `${aggregatedResult.clientsCreated} cliente(s) criado(s)`,
        `${aggregatedResult.servicesCreated} serviço(s) criado(s)`,
      ];
      if (aggregatedResult.servicesUpdated != null && aggregatedResult.servicesUpdated > 0) {
        parts.push(`${aggregatedResult.servicesUpdated} atualizado(s)`);
      }
      if (aggregatedResult.servicesDeleted != null) {
        parts.push(`${aggregatedResult.servicesDeleted} excluído(s)`);
      }
      notifySuccess(`Importação concluída: ${parts.join(', ')}.`);
      dispatch('fetchClients');
    } catch (error) {
      const message =
        (error as any)?.response?.data?.error ??
        (error as any)?.response?.data?.message ??
        'Erro ao executar importação';
      notifyError(typeof message === 'string' ? message : 'Erro ao executar importação');
      console.error('Erro ao executar importação:', error);
      throw error;
    }
  },
};



export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
